import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'

const SESSION_KEY = 'ee_session_id'
const LOCAL_PREDS_KEY = 'ee_predictions'

function getSessionId() {
  let id = localStorage.getItem(SESSION_KEY)
  if (!id) {
    id = 'anon_' + Math.random().toString(36).substr(2, 9)
    localStorage.setItem(SESSION_KEY, id)
  }
  return id
}

function getLocalPredictions() {
  try { return JSON.parse(localStorage.getItem(LOCAL_PREDS_KEY) || '[]') }
  catch { return [] }
}

function saveLocalPrediction(pred) {
  const preds = getLocalPredictions()
  const existing = preds.findIndex(p => p.ticker === pred.ticker && p.report_date === pred.report_date)
  if (existing >= 0) preds[existing] = pred
  else preds.push(pred)
  localStorage.setItem(LOCAL_PREDS_KEY, JSON.stringify(preds))
}

function deleteLocalPrediction(ticker, report_date) {
  const preds = getLocalPredictions().filter(p => !(p.ticker === ticker && p.report_date === report_date))
  localStorage.setItem(LOCAL_PREDS_KEY, JSON.stringify(preds))
}

export function usePredictions() {
  const { user } = useAuth()
  const [predictions, setPredictions] = useState([])

  const loadPredictions = useCallback(async () => {
    if (user) {
      const { data } = await supabase
        .from('predictions').select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
      setPredictions(data || [])
    } else {
      setPredictions(getLocalPredictions())
    }
  }, [user])

  useEffect(() => { loadPredictions() }, [loadPredictions])

  const submitPrediction = async ({ ticker, company_name, report_date, report_time, direction, mode, magnitude_bucket }) => {
    const pred = { ticker, company_name, report_date, report_time, direction, mode, magnitude_bucket }

    if (user) {
      // Optimistic update — show result instantly, no waiting
      const optimistic = { ...pred, user_id: user.id, id: `opt_${ticker}_${report_date}`, created_at: new Date().toISOString() }
      setPredictions(prev => [optimistic, ...prev.filter(p => !(p.ticker === ticker && p.report_date === report_date))])
      const { error } = await supabase.from('predictions').upsert(
        { ...pred, user_id: user.id },
        { onConflict: 'user_id,ticker,report_date' }
      )
      if (error) await loadPredictions() // revert on error
      return { error }
    } else {
      const localPred = { ...pred, id: `local_${ticker}_${report_date}`, session_id: getSessionId(), created_at: new Date().toISOString() }
      saveLocalPrediction(localPred)
      setPredictions(getLocalPredictions())
      return { error: null, isAnonymous: true }
    }
  }

  const deletePrediction = async (ticker, report_date) => {
    if (user) {
      // Optimistic removal
      setPredictions(prev => prev.filter(p => !(p.ticker === ticker && p.report_date === report_date)))
      await supabase.from('predictions').delete()
        .eq('user_id', user.id).eq('ticker', ticker).eq('report_date', report_date)
    } else {
      deleteLocalPrediction(ticker, report_date)
      setPredictions(getLocalPredictions())
    }
  }

  const getPredictionForStock = (ticker, report_date) =>
    predictions.find(p => p.ticker === ticker && p.report_date === report_date)

  const migrateAnonymousPredictions = async (userId) => {
    const local = getLocalPredictions()
    if (local.length === 0) return
    const toInsert = local.map(p => ({ ...p, user_id: userId, id: undefined, session_id: undefined }))
    await supabase.from('predictions').insert(toInsert)
    localStorage.removeItem(LOCAL_PREDS_KEY)
  }

  const getStats = () => {
    const scored = predictions.filter(p => p.is_correct !== null && p.is_correct !== undefined)
    if (scored.length === 0) return null
    const correct = scored.filter(p => p.direction_correct)
    const incorrect = scored.filter(p => !p.direction_correct)
    const avgMoveCorrect = correct.length > 0 ? correct.reduce((s, p) => s + Math.abs(p.actual_change || 0), 0) / correct.length : 0
    const avgMoveIncorrect = incorrect.length > 0 ? incorrect.reduce((s, p) => s + Math.abs(p.actual_change || 0), 0) / incorrect.length : 0
    return {
      total: scored.length, correct: correct.length, incorrect: incorrect.length,
      accuracy: Math.round((correct.length / scored.length) * 100),
      avgMoveCorrect: avgMoveCorrect.toFixed(1),
      avgMoveIncorrect: avgMoveIncorrect.toFixed(1),
      pending: predictions.filter(p => p.is_correct === null || p.is_correct === undefined).length,
    }
  }

  return { predictions, submitPrediction, deletePrediction, getPredictionForStock, migrateAnonymousPredictions, getStats, reload: loadPredictions }
}
